import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState({ categories: [], brands: [] });
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("q") || "");
  const [cats, setCats] = useState(params.get("categoria") ? [params.get("categoria")] : []);
  const [brands, setBrands] = useState([]);
  const [sort, setSort] = useState("new");

  useEffect(() => {
    api.get("/products/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setSearch(params.get("q") || "");
    setCats(params.get("categoria") ? [params.get("categoria")] : []);
  }, [params]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: { search, category: cats.join(","), brand: brands.join(","), sort, limit: 60 } })
      .then((r) => {
        setProducts(r.data.items);
        setTotal(r.data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, cats, brands, sort]);

  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const submitSearch = (e) => {
    e.preventDefault();
    setParams(search ? { q: search } : {});
  };

  return (
    <div data-testid="catalog-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="overline-tag">Catalogo</div>
      <h1 className="mt-3 font-display font-medium uppercase tracking-tight text-4xl md:text-5xl">
        Trova il tuo ricambio
      </h1>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-4 gap-10">
        <aside className="lg:col-span-1 lg:sticky lg:top-40 self-start flex flex-col gap-8">
          <form onSubmit={submitSearch} role="search">
            <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Cerca</div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={2} />
              <input
                data-testid="catalog-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nome, marca, passo..."
                className="w-full border-2 border-[#0a0a0a] bg-white pl-10 pr-4 py-2.5 text-sm outline-none focus:border-[#ff4500] transition-colors"
              />
            </div>
          </form>

          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Categoria</div>
            <div className="flex flex-col gap-2.5">
              {meta.categories.map((c) => (
                <label key={c} className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <Checkbox
                    data-testid={`filter-category-${c.toLowerCase().replace(/\s/g, "-")}`}
                    checked={cats.includes(c)}
                    onCheckedChange={() => toggle(cats, setCats, c)}
                  />
                  {c}
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Marca</div>
            <div className="flex flex-col gap-2.5">
              {meta.brands.filter(Boolean).map((b) => (
                <label key={b} className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <Checkbox
                    data-testid={`filter-brand-${b.toLowerCase().replace(/\s/g, "-")}`}
                    checked={brands.includes(b)}
                    onCheckedChange={() => toggle(brands, setBrands, b)}
                  />
                  {b}
                </label>
              ))}
            </div>
          </div>
        </aside>

        <div className="lg:col-span-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p data-testid="catalog-results-count" className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{total}</span> prodotti trovati
            </p>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="catalog-sort-select" className="w-52 rounded-none border-2 border-[#0a0a0a]">
                <SelectValue placeholder="Ordina per" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Novità</SelectItem>
                <SelectItem value="price_asc">Prezzo crescente</SelectItem>
                <SelectItem value="price_desc">Prezzo decrescente</SelectItem>
                <SelectItem value="name">Nome A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div data-testid="catalog-loading" className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white border border-[#e5e5e5] aspect-[3/4] animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div data-testid="catalog-empty" className="mt-16 text-center py-20 border border-dashed border-[#e5e5e5] bg-white">
              <p className="font-display text-2xl uppercase tracking-tight">Nessun prodotto trovato</p>
              <p className="mt-2 text-sm text-muted-foreground">Prova a modificare i filtri o il termine di ricerca.</p>
            </div>
          ) : (
            <div data-testid="catalog-products-grid" className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p, i) => (
                <ProductCard key={p.id} p={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
